import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type {
  CreateSoundClipPayload,
  FeatureCode,
  SoundClip,
  SoundClipApi,
  SoundClipListParams,
  UpdateSoundClipPayload,
} from '../api/soundClipApi.types'

const QUERY_KEYS = {
  all: (pbxId: number) => ['soundClips', pbxId] as const,
  list: (params: SoundClipListParams) =>
    ['soundClips', params.pbxId, params] as const,
  detail: (id: number) => ['soundClip', id] as const,
  takenFeatureCodes: (pbxId: number) =>
    ['soundClips', pbxId, 'takenFeatureCodes'] as const,
}

export { QUERY_KEYS as SOUND_CLIP_QUERY_KEYS }

export const useGetSoundClips = (
  api: SoundClipApi,
  params: SoundClipListParams | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.list(params ?? { pbxId: 0 }),
    queryFn: () => api.getSoundClips(params!),
    enabled: !!params?.pbxId,
    retry: false,
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Returns the feature codes (>0) currently assigned to sound clips in the PBX,
 * along with the clip name they're assigned to (so the picker can show
 * "*100 (taken by Main Greeting)").
 *
 * Backend has no `feature_code` filter today, so this fetches all clips in
 * the PBX with limit=999 and filters client-side. Bounded in practice — most
 * PBXes have well under 100 clips, and feature codes only span 0 + 100–109.
 */
export interface TakenFeatureCode {
  code: FeatureCode
  name: string
  id: number
}

export const useGetTakenFeatureCodes = (
  api: SoundClipApi,
  pbxId: number | undefined,
) => {
  return useQuery({
    queryKey: QUERY_KEYS.takenFeatureCodes(pbxId ?? 0),
    queryFn: async (): Promise<TakenFeatureCode[]> => {
      const result = await api.getSoundClips({ pbxId: pbxId!, limit: 999 })
      return result.results
        .filter((clip: SoundClip) => clip.feature_code !== 0)
        .map((clip) => ({
          code: clip.feature_code,
          name: clip.name,
          id: clip.id,
        }))
    },
    enabled: !!pbxId,
    retry: false,
  })
}

export const useGetSoundClip = (api: SoundClipApi, id: number | undefined) => {
  return useQuery({
    queryKey: QUERY_KEYS.detail(id ?? 0),
    queryFn: () => api.getSoundClip(id!),
    enabled: !!id,
    retry: false,
  })
}

export const useCreateSoundClip = (
  api: SoundClipApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload: CreateSoundClipPayload) =>
      api.createSoundClip(payload),
    onSuccess: () => {
      if (pbxId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all(pbxId) })
      }
    },
  })
}

export const useUpdateSoundClip = (
  api: SoundClipApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number
      payload: UpdateSoundClipPayload
    }) => api.updateSoundClip(id, payload),
    onSuccess: (updatedClip) => {
      queryClient.setQueryData(QUERY_KEYS.detail(updatedClip.id), updatedClip)
      if (pbxId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all(pbxId) })
      }
    },
  })
}

export const useDeleteSoundClip = (
  api: SoundClipApi,
  pbxId: number | undefined,
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => api.deleteSoundClip(id),
    onSettled: () => {
      if (pbxId) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.all(pbxId) })
      }
    },
  })
}
